import { Stack } from "@mui/material";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Text,
  TextSkeleton,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/system/ui-theme";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { DialogFooter } from "../../components/DialogFooter";
import { LoadingButton } from "../../components/LoadingButton";
import { type RenewLicenseModalRouteParams } from "../../Roles/types";
import { useWorkerQualificationsWithLicenses } from "../../Roles/useWorkerQualificationsWithLicenses";
import { shouldShowRenewLicenseInstructions } from "../utils";
import { RenewCNALicenseBottomSheet } from "./RenewCNALicenseBottomSheet";

interface RenewInstructionsContainerProps {
  modalState: UseModalState;
}

export function RenewInstructionsContainer(props: RenewInstructionsContainerProps) {
  const { modalState } = props;

  const { licenseId } = useParams<RenewLicenseModalRouteParams>();
  const { data, isLoading } = useWorkerQualificationsWithLicenses();
  const license = useMemo(() => {
    const matchingQualification = data.find((item) =>
      item.licenses.some((license) => license.id === licenseId)
    );
    return matchingQualification?.licenses.find((license) => license.id === licenseId);
  }, [data, licenseId]);

  if (isLoading) {
    return (
      <BottomSheet
        canDismiss
        disableSwipeArea={false}
        modalState={modalState}
        paperSx={(theme) => ({
          ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
        })}
        header={<BottomSheetHeader onClose={modalState.closeModal} />}
        footer={
          <DialogFooter orientation="vertical">
            <LoadingButton fullWidth isLoading variant="contained" size="large">
              &nbsp;
            </LoadingButton>
          </DialogFooter>
        }
      >
        <Stack spacing={4} sx={{ padding: 7, alignItems: "center", textAlign: "center" }}>
          <TextSkeleton variant="h1" width="100%" />
          <TextSkeleton variant="body1" width="100%" />
          <TextSkeleton variant="body1" width="100%" />
          <TextSkeleton variant="body1" width="100%" />
          <TextSkeleton variant="body1" width="100%" />
          <TextSkeleton variant="body1" width="100%" />
        </Stack>
      </BottomSheet>
    );
  }

  if (license?.attributes && shouldShowRenewLicenseInstructions(license.attributes)) {
    return (
      <RenewCNALicenseBottomSheet modalState={modalState} licenseAttributes={license.attributes} />
    );
  }

  // Should never happen but just in case
  return (
    <BottomSheet
      modalState={modalState}
      paperSx={(theme) => ({
        ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
      })}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
    >
      <BottomSheetIllustrationContent
        illustrationType="warning"
        title="No renewal instructions available"
        description={
          <Text>
            Looks like you wandered into the wrong page. No renewal instructions are available for
            this license. Contact support if you think this is an error.
          </Text>
        }
      />
    </BottomSheet>
  );
}
